import React, { useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence, useDragControls, useReducedMotion } from "framer-motion";
import { getSheetBackdropMotion, getSheetPanelMotion, useSheetSlideIn } from "../../utils/sheetMotion";
import { AiOutlineSync } from "react-icons/ai";
import { FiX, FiPlay, FiPlus, FiMinus } from "react-icons/fi";
import { FaGraduationCap } from "react-icons/fa";
import { RenderMarkdownWithLatex, truncateExample } from "../../utils/utils";
import { LEVEL_DEFINITIONS, getThresholdsForField } from "../../utils/gamificationConfig";

// --- GAMIFICATION TIMELINE ---
const RankTimeline = ({ field }) => {
  const thresholds = getThresholdsForField(field.field_id);
  const elo = field.user_field_elo || 0;
  const solved = field.total_solved_per_field || 0;

  const levels = LEVEL_DEFINITIONS.map((levelDef) => {
    const req = thresholds[levelDef.name] || { elo: 0, tasks: 0 };
    const isAchieved = elo >= req.elo && solved >= req.tasks;
    return { ...levelDef, req, isAchieved };
  });

  return (
    <div className="pt-2 pb-1 px-1">
      <div className="flex justify-between items-start">
        {levels.map((level, index) => (
          <React.Fragment key={level.name}>
            <div className="flex flex-col items-center w-14 flex-shrink-0">
              <div className="h-14 flex items-center justify-center">
                <img
                  src={level.isAchieved ? level.icon.achieved : level.icon.notAchieved}
                  alt={level.name}
                  className={`w-12 h-12 ${!level.isAchieved ? "opacity-40" : ""}`}
                />
              </div>
              <span className={`text-[11px] text-center leading-tight whitespace-nowrap ${level.isAchieved ? "text-[#233855] font-semibold" : "text-gray-400"}`}>
                {level.name}
              </span>
              <span className="text-[11px] text-center text-gray-400 leading-snug">ELO {level.req.elo}+</span>
              <span className="text-[11px] text-center text-gray-400 leading-snug">{level.req.tasks}+ zad.</span>
            </div>
            {index < levels.length - 1 && (
              <div className={`flex-1 h-px self-start mt-7 ${level.isAchieved ? "bg-[#233855]" : "bg-gray-200"}`} />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

// --- WIERSZ TEMATU ---
// Jednostka koszyka: na podstawie podtemat, na rozszerzeniu szablon. Backend
// przysyła oba w tym samym kształcie (na PR w `subfield_id` jedzie template_id,
// a próg ELO jest przeniesiony z podtematu), więc to jeden komponent na oba poziomy.
const SubfieldRow = ({ subfield, inBasket, onToggle, onDirectStart }) => {
  const hasSources = subfield.sources && subfield.sources.length > 0;
  const hasExample = !!subfield.example_problem;
  const hasPoints = !!subfield.points;

  return (
    <div className={`border-b border-gray-100 last:border-0 py-3 transition-colors ${inBasket ? "bg-blue-50/50" : ""}`}>
      {/* Nazwa + ELO threshold */}
      <div className="flex items-start justify-between gap-2 mb-1.5 px-1">
        <p className="text-sm font-medium text-gray-800 leading-snug">
          {subfield.subfield_name}
        </p>
        <span className="flex-shrink-0 text-xs text-gray-400 font-medium pt-0.5">
          ELO tematu: {subfield.elo_threshold > 0 ? subfield.elo_threshold : 10}
        </span>
      </div>

      {/* Punkty + liczba zadań + odznaki CKE */}
      {(hasPoints || hasSources || subfield.problems_count) && (
        <div className="flex flex-wrap items-center gap-1 mb-2 px-1">
          {hasPoints && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-amber-50 border border-amber-100 text-amber-700 text-[11px] font-bold whitespace-nowrap">
              {String(subfield.points).replace(' pkt', '')} pkt
            </span>
          )}
          {!!subfield.problems_count && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-gray-100 border border-gray-200 text-[11px] text-gray-500 whitespace-nowrap">
              {subfield.problems_count} zad.
            </span>
          )}
          {hasSources && subfield.sources.map((src, idx) => (
            <span
              key={idx}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-100 border border-gray-200 text-[11px] text-gray-500 whitespace-nowrap"
            >
              <FaGraduationCap className="text-[#233855]" size={8} />
              {src.replace('Zadanie', 'Zad.')}
            </span>
          ))}
        </div>
      )}

      {/* Przykładowe zadanie */}
      {hasExample && (
        <div className="mb-3 px-1">
          <p className="text-[11px] text-gray-400 uppercase tracking-widest font-semibold mb-1 flex items-center gap-1">
            <FaGraduationCap size={8} /> Przykładowe zadanie
          </p>
          <div className="p-2.5 bg-gray-50 rounded-lg text-sm text-gray-600 border border-gray-100">
            <RenderMarkdownWithLatex text={truncateExample(subfield.example_problem.question)} />
          </div>
        </div>
      )}

      {/* Przyciski akcji */}
      <div className="flex gap-2 px-1">
        <button
          type="button"
          onClick={() => onToggle(subfield.subfield_id)}
          className={`flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold py-2 rounded-lg transition-colors ${
            inBasket
              ? "bg-red-50 border border-red-200 text-red-600 hover:bg-red-100"
              : "bg-gray-100 border border-gray-200 text-gray-700 hover:bg-gray-200"
          }`}
        >
          {inBasket
            ? <><FiMinus className="w-3 h-3" /> Usuń z treningu</>
            : <><FiPlus className="w-3 h-3" /> Dodaj do treningu</>
          }
        </button>

        <button
          type="button"
          onClick={() => onDirectStart(subfield.subfield_id)}
          className="flex-shrink-0 flex items-center gap-1.5 bg-[#233855] hover:bg-[#2a4263] text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors"
        >
          <FiPlay className="w-3 h-3" />
          Start
        </button>
      </div>
    </div>
  );
};

// --- GŁÓWNY KOMPONENT SHEETU ---
const FieldSheet = ({ isOpen, field, subfields, isLoading, basket, onToggleBasket, onDirectStart, onClose }) => {
  const dragControls = useDragControls();
  const prefersReducedMotion = useReducedMotion();
  const fieldRef = useRef(field);

  useEffect(() => {
    if (isOpen) {
      window.dispatchEvent(new CustomEvent('bottomsheet:open'));
      return () => window.dispatchEvent(new CustomEvent('bottomsheet:close'));
    }
  }, [isOpen]);
  if (field) fieldRef.current = field;
  const currentField = fieldRef.current;
  const slideIn = useSheetSlideIn(isOpen && !!currentField);

  return ReactDOM.createPortal(
    <AnimatePresence>
      {isOpen && currentField && (
        <>
          <motion.div
            key="field-backdrop"
            {...getSheetBackdropMotion()}
            className="fixed inset-0 bg-black/50 z-[9990]"
            onClick={onClose}
          />
          <motion.div
            key="field-sheet"
            {...getSheetPanelMotion(prefersReducedMotion, slideIn)}
            drag="y"
            dragControls={dragControls}
            dragListener={false}
            dragConstraints={{ top: 0 }}
            dragElastic={{ top: 0, bottom: 0.4 }}
            onDragEnd={(_, info) => { if (info.offset.y > 100 || info.velocity.y > 500) onClose(); }}
            className="fixed bottom-0 left-0 right-0 z-[9991] bg-white rounded-t-2xl shadow-[0_-4px_30px_rgba(0,0,0,0.2)] flex flex-col max-h-[92vh] lg:max-w-2xl lg:mx-auto lg:rounded-2xl"
          >
            {/* Drag handle */}
            <div
              className="flex justify-center pt-3 pb-1 flex-shrink-0 cursor-grab touch-none"
              onPointerDown={(e) => dragControls.start(e)}
            >
              <div className="w-10 h-1 bg-gray-200 rounded-full" />
            </div>

            {/* Nagłówek */}
            <div className="flex-shrink-0 px-5 pt-3 pb-0">
              <div className="flex items-start justify-between gap-3 mb-0.5">
                <div className="flex-1 min-w-0">
                  <h2 className="font-apotek text-lg text-[#233855] leading-tight">
                    {currentField.field_name}
                  </h2>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Twoje ELO: <span className="font-semibold text-[#233855]">{currentField.user_field_elo || 0}</span>
                    &nbsp;·&nbsp;{currentField.total_solved_per_field || 0} rozwiązanych zadań
                  </p>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                >
                  <FiX className="w-4 h-4 text-gray-600" />
                </button>
              </div>

              {/* Timeline rang */}
              <RankTimeline field={currentField} />

            </div>

            {/* Lista subfieldów (scrollowalna) */}
            <div className="flex-1 overflow-y-auto overscroll-contain px-5 pb-6 border-t border-gray-100">
              {isLoading && (
                <div className="flex items-center justify-center py-12 gap-2 text-gray-400 text-sm">
                  <AiOutlineSync className="animate-spin w-4 h-4" />
                  Ładowanie tematów...
                </div>
              )}

              {!isLoading && subfields.length === 0 && (
                <p className="text-center text-sm text-gray-400 py-8">Brak tematów.</p>
              )}

              {!isLoading && subfields.map((subfield) => (
                <SubfieldRow
                  key={subfield.subfield_id}
                  subfield={subfield}
                  inBasket={basket.includes(subfield.subfield_id)}
                  onToggle={onToggleBasket}
                  onDirectStart={onDirectStart}
                />
              ))}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default FieldSheet;
